import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';

import {
  GLOBAL_MANUFACTURERS_FOR_DROPDOWN,
  GLOBAL_MANUFACTURERS_GET_BY_ID,
  getURL,
} from 'lib/networking/endpoints';
import SearchableScrollableSelect from 'components/shared/SearchableScrollableSelect';
import useGet from 'lib/hooks/useGet';

function GDSNSelectManufacturerModal({ show, onHide, dataSource, processDataSource, refetchRun }) {
  const [selectedManufacturerOption, setSelectedManufacturerOption] = useState(null);
  const [selectedGlns, setSelectedGlns] = useState([]);
  const [lookbackDays, setLookbackDays] = useState(dataSource?.lookback_days || 30);

  const { data: { manufacturer: manufacturerDetails } = {}, loading: loadingManufacturer } = useGet(
    getURL(GLOBAL_MANUFACTURERS_GET_BY_ID, {
      manufacturer_id: selectedManufacturerOption?.value,
    }),
    {},
    !selectedManufacturerOption?.value,
  );

  // Reset modal state when it's closed
  useEffect(() => {
    if (!show) {
      setSelectedManufacturerOption(null);
      setSelectedGlns([]);
      setLookbackDays(dataSource?.lookback_days || 30);
    }
  }, [show, dataSource]);

  // Set default GLNs when manufacturer details are loaded
  useEffect(() => {
    if (
      manufacturerDetails?.manufacturer_glns &&
      Object.keys(manufacturerDetails.manufacturer_glns).length > 0
    ) {
      setSelectedGlns([Object.keys(manufacturerDetails.manufacturer_glns)[0]]);
    } else {
      setSelectedGlns([]);
    }
  }, [manufacturerDetails]);

  const handleManufacturerSelect = selectedOption => {
    setSelectedManufacturerOption(selectedOption || null);
  };

  const handleGlnChange = gln => {
    setSelectedGlns(prev => {
      if (prev.includes(gln)) {
        return prev.filter(g => g !== gln);
      } else {
        return [...prev, gln];
      }
    });
  };

  const handleSubmit = () => {
    if (!manufacturerDetails || selectedGlns.length === 0) return;

    processDataSource({
      data_source_id: dataSource.id,
      manufacturer_id: manufacturerDetails.id,
      manufacturer_glns: selectedGlns,
      lookback_days: lookbackDays,
      process_delta_file: false,
    }).then(() => {
      setTimeout(() => {
        refetchRun();
      }, 1000);
    });
    onHide();
  };
  const renderModalContent = () => (
    <div>
      <Form.Group>
        <Form.Label>Select Manufacturer</Form.Label>
        <SearchableScrollableSelect
          initOption={selectedManufacturerOption}
          onChange={handleManufacturerSelect}
          fetchUrl={GLOBAL_MANUFACTURERS_FOR_DROPDOWN}
          optionMapper={manufacturer => ({
            value: manufacturer.id,
            label: manufacturer.name,
          })}
          autoFocus={false}
          placeholder="Search and select a manufacturer..."
        />
      </Form.Group>

      {selectedManufacturerOption && (
        <div className="mt-4">
          {loadingManufacturer ? (
            <div className="d-flex justify-content-center">
              <Spinner animation="border" variant="primary" className="mx-auto" />
            </div>
          ) : manufacturerDetails ? (
            <>
              <Form.Group>
                <Form.Label>Select GLNs</Form.Label>
                <div className="mt-2">
                  {manufacturerDetails.manufacturer_glns &&
                    Object.entries(manufacturerDetails.manufacturer_glns).map(
                      ([gln, subCompanyName]) => (
                        <Form.Check
                          type="checkbox"
                          key={gln}
                          id={`gln-${gln}`}
                          label={`${gln} ${subCompanyName ? `(${subCompanyName})` : ''}`}
                          name="glnOptions"
                          value={gln}
                          checked={selectedGlns.includes(gln)}
                          onChange={() => handleGlnChange(gln)}
                          className="mb-2"
                        />
                      ),
                    )}
                </div>
                {(!manufacturerDetails.manufacturer_glns ||
                  Object.keys(manufacturerDetails.manufacturer_glns).length === 0) && (
                  <Alert variant="warning">This manufacturer has no GLNs available.</Alert>
                )}
              </Form.Group>

              <Form.Group className="mt-3">
                <Form.Label>Lookback Days</Form.Label>
                <Form.Control
                  type="number"
                  value={lookbackDays}
                  onChange={e => setLookbackDays(parseInt(e.target.value, 10))}
                  min="1"
                />
              </Form.Group>
            </>
          ) : (
            <Alert variant="info">Loading manufacturer details...</Alert>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <strong>Process Manufacturer GDSN Data</strong>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>{renderModalContent()}</Modal.Body>
      <Modal.Footer>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!manufacturerDetails || selectedGlns.length === 0 || loadingManufacturer}
        >
          Process
        </Button>
        <Button variant="outline-primary" onClick={onHide}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

GDSNSelectManufacturerModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  dataSource: PropTypes.object.isRequired,
  processDataSource: PropTypes.func.isRequired,
  refetchRun: PropTypes.func.isRequired,
};

export default GDSNSelectManufacturerModal;
